import { useGlobalLoading } from '../../../../hooks/loadingHooks';
import { useAppSelector } from '../../../../reducers';
import { isAppAuthSelector } from '../../../../selectors/AuthSelectors';
import { packagedQuantityBySkuIdSelector } from '../../../../selectors/ProductsSelectors';
import { useQueryParamTerm } from '../../../../utils/urls/appQueryParamUtilsLegacy';
import { MerchCardLayoutUI } from '../../../../v2/components/MerchCardLayoutUI';
import { IncludedHubSpotCredits } from '../../../../v2/components/merch-card/credits-display/IncludedHubSpotCredits';
import { STARTER_CUSTOMER_PLATFORM_UPSELL_MERCH_CARD } from '../../../../v2/constants/tracking/TrackingSources';
import DetailedTermSelector from '../../../common/TermSelectors/DetailedTermSelector';
import SkeletonLoadWrapper from '../../../common/loading/SkeletonLoadWrapper';
import { PriceDisplayContainer } from '../../price-display/PriceDisplayContainer';
import { useCrmSuiteBundleApiName, useCrmSuiteBundleSkuId } from '../../price-display/useCrmSuite';
import { SubtextDisplayContainerV1 } from '../../subtext-display/SubtextDisplayContainerV1';
import { useSkuAdditionalUnits } from '../../useSkuAdditionalUnits';
import { SuiteCardCTAs } from './SuiteCardCTAs';
import { SuiteCardSubheading } from './SuiteCardSubheading';
import { SuiteCardTitle } from './SuiteCardTitle';

/*
 * This card is rendered on the public starter tab on hub pages
 * and for free TB portals in app.
 * Therefore there is no pre-seats version of this card.
 */
import { jsx as _jsx } from "react/jsx-runtime";
const UPSELL_PRODUCT_LEVEL = 'STARTER';
export const UpsellSuiteCard = ({
  productLine,
  screen,
  columnStart,
  Badge,
  Features
}) => {
  const isAppReady = useGlobalLoading();
  const isAppAuth = useAppSelector(isAppAuthSelector);
  const {
    queryParamTerm,
    setQueryParamTerm
  } = useQueryParamTerm();

  /*
   * This is the skuId for product line the upsell card is currently showing,
   * not the skuId for the bundle product
   * So if you're on the marketing page, this will be the marketing contacts skuId
   */
  const {
    additionalUnitSkuId
  } = useSkuAdditionalUnits({
    productLevel: UPSELL_PRODUCT_LEVEL,
    productLine
  });
  const suiteSkuId = useCrmSuiteBundleSkuId({
    productLevel: UPSELL_PRODUCT_LEVEL
  });
  const suiteApiName = useCrmSuiteBundleApiName({
    productLevel: UPSELL_PRODUCT_LEVEL
  });
  const includedProductLevelHubspotCredits = useAppSelector(state => {
    if (!suiteSkuId) return null;
    return packagedQuantityBySkuIdSelector(state, {
      skuId: suiteSkuId,
      unitName: 'HUBSPOT_CREDITS'
    });
  });
  const trackingSource = STARTER_CUSTOMER_PLATFORM_UPSELL_MERCH_CARD;
  const TermSelector = /*#__PURE__*/_jsx(SkeletonLoadWrapper, {
    isLoading: !isAppReady,
    heights: [41],
    widths: ['100%'],
    children: suiteApiName ? /*#__PURE__*/_jsx(DetailedTermSelector, {
      apiName: suiteApiName,
      onChange: setQueryParamTerm,
      activeTerm: queryParamTerm,
      trackingSource: trackingSource
    }) : null
  });
  const Credits = /*#__PURE__*/_jsx(SkeletonLoadWrapper, {
    isLoading: !isAppReady,
    heights: [34],
    widths: ['75%'],
    children: !!includedProductLevelHubspotCredits && /*#__PURE__*/_jsx(IncludedHubSpotCredits, {
      includedProductLevelHubspotCredits: includedProductLevelHubspotCredits,
      trackingSource: trackingSource
    })
  });
  const Subtext = /*#__PURE__*/_jsx(SkeletonLoadWrapper, {
    isLoading: !isAppReady,
    heights: [34],
    widths: ['75%'],
    children: /*#__PURE__*/_jsx(SubtextDisplayContainerV1, {
      additionalUnitSkuId: additionalUnitSkuId,
      queryParamTerm: queryParamTerm,
      productLine: productLine,
      productLevel: UPSELL_PRODUCT_LEVEL
    })
  });
  return /*#__PURE__*/_jsx(MerchCardLayoutUI, {
    "data-test-id": "crm-suite-starter-bundle-merch-card",
    "data-selenium-id": "crm-suite-starter-bundle-merch-card",
    columnStart: columnStart,
    Badge: Badge,
    Title: /*#__PURE__*/_jsx(SuiteCardTitle, {
      productLevel: UPSELL_PRODUCT_LEVEL,
      showProductLevelTitle: true
    }),
    Subheading: /*#__PURE__*/_jsx(SuiteCardSubheading, {
      productLevel: UPSELL_PRODUCT_LEVEL
    }),
    Pricing: !!suiteSkuId && /*#__PURE__*/_jsx(PriceDisplayContainer, {
      skuId: suiteSkuId,
      queryParamTerm: queryParamTerm,
      apiName: suiteApiName
    }),
    Term: TermSelector,
    Credits: Credits,
    Subtext: Subtext,
    CTAs: /*#__PURE__*/_jsx(SuiteCardCTAs, {
      productLevel: UPSELL_PRODUCT_LEVEL,
      screen: screen,
      trackingSource: trackingSource,
      returnFirstCTAOnly: isAppAuth
    }),
    Features: Features
  });
};